import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useGameCountdown - A custom hook for managing game countdown timers
 * 
 * @param {Object} options
 * @param {number} [options.initialCount=3] - Starting countdown value
 * @param {number} [options.interval=1000] - Milliseconds between each count
 * @param {boolean} [options.autoStart=true] - Whether to start countdown immediately
 * @param {number} [options.delayAfterZero=0] - Extra delay (ms) after countdown reaches 0 before calling onComplete
 * @param {Function} [options.onTick] - Callback fired on each tick with current count
 * @param {Function} [options.onComplete] - Callback fired when countdown completes
 * @param {Function} [options.onStart] - Callback fired when countdown starts
 * 
 * @returns {Object} Countdown state and controls
 * @returns {number} returns.countdown - Current countdown value
 * @returns {boolean} returns.isCountingDown - Whether countdown is active
 * @returns {boolean} returns.isComplete - Whether countdown has finished
 * @returns {Function} returns.start - Function to start/restart the countdown
 * @returns {Function} returns.pause - Function to pause the countdown
 * @returns {Function} returns.reset - Function to reset to initial state
 */
const useGameCountdown = ({
  initialCount = 3,
  interval = 1000,
  autoStart = true,
  delayAfterZero = 0,
  onTick,
  onComplete,
  onStart
} = {}) => {
  const [countdown, setCountdown] = useState(initialCount);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const timerRef = useRef(null);
  const startedRef = useRef(false);
  
  // Clear any existing timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Start or restart the countdown
  const start = useCallback(() => {
    clearTimer();
    setCountdown(initialCount);
    setIsCountingDown(true);
    setIsComplete(false);
    startedRef.current = true;
    
    if (onStart) {
      onStart();
    }
  }, [initialCount, clearTimer, onStart]);

  // Pause the countdown
  const pause = useCallback(() => {
    clearTimer();
    setIsCountingDown(false);
  }, [clearTimer]);

  // Reset to initial state without starting
  const reset = useCallback(() => {
    clearTimer();
    setCountdown(initialCount);
    setIsCountingDown(false);
    setIsComplete(false);
    startedRef.current = false;
  }, [initialCount, clearTimer]);

  // Handle countdown logic
  useEffect(() => {
    if (!isCountingDown) return;

    if (countdown > 0) {
      timerRef.current = setTimeout(() => {
        const newCount = countdown - 1;
        setCountdown(newCount);
        
        if (onTick) {
          onTick(newCount);
        }
      }, interval);
    } else if (countdown === 0) {
      // Countdown reached zero
      const completeDelay = delayAfterZero > 0 ? delayAfterZero : 0;
      
      timerRef.current = setTimeout(() => {
        setIsCountingDown(false);
        setIsComplete(true);
        
        if (onComplete) {
          onComplete();
        }
      }, completeDelay);
    }

    return () => clearTimer();
  }, [countdown, isCountingDown, interval, delayAfterZero, onTick, onComplete, clearTimer]);

  // Auto-start on mount if enabled
  useEffect(() => {
    if (autoStart && !startedRef.current) {
      start();
    }
    
    return () => clearTimer();
  }, [autoStart, start, clearTimer]);

  return {
    countdown,
    isCountingDown,
    isComplete,
    start,
    pause,
    reset
  };
};

export default useGameCountdown;
